import { resetPassword } from '@/api/usersAPI'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material'
import { useState } from 'react'

interface ResetPasswordProps {
  email: string
  showResetPasswordModal: boolean
  setShowResetPasswordModal: React.Dispatch<React.SetStateAction<boolean>>
  token: string | null
}

export default function ResetPassword({
  email,
  showResetPasswordModal,
  setShowResetPasswordModal,
  token,
}: ResetPasswordProps) {
  const [error, setError] = useState<string | null>(null)
  const [isSending, setIsSending] = useState(false)
  const [isSent, setIsSent] = useState(false)

  async function handleResetPassword() {
    setError(null)

    if (!email) {
      setError('Sorry, something went wrong! Please try again.')
      return
    }

    if (!token) {
      setError('Sorry, something went wrong! Please try again.')
      return
    }

    try {
      setIsSending(true)
      await resetPassword(token)

      setIsSending(false)
      setIsSent(true)
    } catch (err) {
      if (err instanceof Error) {
        setError('Sorry, something went wrong! Please try again.')
      }
    } finally {
      setIsSending(false)
    }
  }

  return (
    <Dialog
      open={showResetPasswordModal}
      onClose={() => setShowResetPasswordModal(false)}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle sx={{ textAlign: 'center' }}>Reset Password</DialogTitle>

      <DialogContent sx={{ width: '100%', textAlign: 'center' }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          We'll send a password reset link to your email.
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          To keep your account secure, password changes are handled through a
          secure link sent to your email address. Once you click the link,
          you’ll be able to choose a new password.
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          The reset link will be sent to{' '}
          <span style={{ fontWeight: '500' }}>{email}</span>
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {isSent && (
          <Alert severity="success" sx={{}}>
            Password reset link sent
          </Alert>
        )}
        <Button
          variant="outlined"
          disableRipple
          onClick={() => setShowResetPasswordModal(false)}
          sx={{
            mr: 1,
            textTransform: 'none',
            color: '#1C3045',
            fontSize: '16px',
            borderColor: '#1C3045',
            border: 2,
            borderRadius: 2,
            '&:focus': {
              backgroundColor: '#e8e8e8',
            },
          }}
        >
          {isSent ? 'Close' : 'Cancel'}
        </Button>
        <Button
          type="submit"
          variant="contained"
          disableRipple
          disabled={isSending || !email || isSent}
          startIcon={
            isSending ? (
              <CircularProgress size={20} color="inherit" />
            ) : undefined
          }
          sx={{
            borderRadius: 2,
            bgcolor: '#1C3045',
            color: 'white',
            textTransform: 'none',
            fontSize: '16px',
            boxShadow: 'none',
          }}
          onClick={() => handleResetPassword()}
        >
          {isSending ? 'Sending...' : 'Send Reset Link'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
